'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

import { AdaptiveLogo } from '@/components/adaptive-logo';

import type { PortfolioShowcaseItem } from '../components/PortfolioShowcase';

import { CaseStudyCTA } from '../components';
import { PortfolioShowcase } from '../components/PortfolioShowcase';
import { ServiceAllCapsTitle } from '../components/ServiceAllCapsTitle';
import { ServiceLayout } from '../components/ServiceLayout';
import contentJson from './content.json';

/** Content shape for proactive-ip/content.json */
interface ProactiveIPContent {
  caseStudy: {
    backgroundImage: string;
    ctaLink: string;
    ctaText: string;
    paragraph: string;
    paragraphLink?: { text: string; url: string };
    quote: string;
    testimonialPerson: string;
    testimonialTitle: string;
    title: string;
  };
  faq: {
    items: Array<{ answer: { paragraphs: string[] }; question: string }>;
    sectionTitle: string;
  };
  hero: {
    backgroundColor?: string;
    backgroundImage?: string;
    heading: string;
    headingStatement?: string;
  };
  iconGrid: {
    paragraphs: string[];
    sectionTitle: string;
  };
  intro: {
    ctaLink: string;
    ctaText: string;
    heading: string;
    paragraphs: string[];
    testimonial: {
      person: string;
      photo: string;
      photoAlt: string;
      quote: string;
      title: string;
    };
  };
  layout: { logoColor: string };
  portfolioShowcase: PortfolioShowcaseItem[];
  process: {
    sectionTitle: string;
    paragraph: string;
    steps: Array<{ description: string; title: string }>;
  };
  stats: {
    introParagraph: string;
    items: Array<{ label: string; value: string }>;
  };
}

const content = contentJson as ProactiveIPContent;

function renderParagraphWithBold(text: string): React.ReactNode {
  const segments: React.ReactNode[] = [];
  const re = /<b>(.*?)<\/b>/g;
  let key = 0;
  let lastEnd = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastEnd) {
      segments.push(text.slice(lastEnd, m.index));
    }
    segments.push(<b key={key++}>{m[1]}</b>);
    lastEnd = m.index + m[0].length;
  }
  if (lastEnd < text.length) {
    segments.push(text.slice(lastEnd));
  }
  return segments.length === 1 && typeof segments[0] === 'string' ? segments[0] : segments;
}

function IntroSection() {
  const { intro: data } = content;
  return (
    <section className="bg-[linear-gradient(to_bottom,transparent_0,transparent_96px,#192836_96px)] pb-12 pt-0 tablet:bg-navy tablet:py-24">
      <div className="relative mx-auto flex w-full max-w-[1328px] flex-col px-4 tablet:flex-row tablet:items-start tablet:gap-16 tablet:px-12">
        <div className="order-1 mb-6 mt-0 w-full min-w-0 bg-white px-4 py-6 tablet:order-1 tablet:my-0 tablet:ml-0 tablet:mr-0 tablet:max-w-[664px] tablet:flex-shrink-0 tablet:bg-transparent tablet:px-0 tablet:py-0">
          <h3 className="mb-4 font-soehne text-serviceSectionHeading font-bold leading-[150%] text-navy tablet:mb-6 tablet:text-white">
            {data.heading}
          </h3>
          <div className="mb-6 space-y-3 tablet:space-y-4">
            {data.paragraphs.map((paragraph, index) => (
              <p
                key={index}
                className="font-soehne text-serviceBody font-normal leading-[150%] text-navy tablet:text-white"
              >
                {renderParagraphWithBold(paragraph)}
              </p>
            ))}
          </div>
          <Link
            href={data.ctaLink}
            className="inline-block rounded-md bg-[#EB4646] px-5 py-3 font-soehne text-serviceCTAText font-normal leading-tight text-[#FAFAFA] transition-colors hover:bg-[#d63a3a] tablet:px-8 tablet:py-4"
          >
            {data.ctaText}
          </Link>
        </div>
        {/* <div className="order-2 flex w-full flex-col gap-6 tablet:order-2 tablet:max-w-[464px] tablet:shrink-0">
          <div className="relative -mx-4 aspect-square w-[100vw] max-w-[100vw] overflow-hidden tablet:mx-auto tablet:w-full tablet:max-w-[464px]">
            <Image
              src={data.testimonial.photo}
              alt={data.testimonial.photoAlt}
              width={464}
              height={464}
              className="h-auto w-full object-cover object-center"
              sizes="(max-width: 767px) 100vw, 464px"
            />
          </div>
          <blockquote className="font-heldane text-serviceQuote font-normal leading-[150%] text-[#FFF]">
            &ldquo;{data.testimonial.quote}&rdquo;
          </blockquote>
          <p className="font-soehne text-serviceCaption font-semibold text-white">
            – {data.testimonial.person}, {data.testimonial.title}
          </p>
        </div> */}
      </div>
    </section>
  );
}

function StatsSection() {
  const { stats: data } = content;
  return (
    <section className="bg-white py-12 tablet:py-24">
      <div className="mx-auto w-full max-w-[1328px] px-4 tablet:px-12">
        <ServiceAllCapsTitle title="BUILDING A STRONGER FOUNDATION" wrapOnMobile />
        <p className="mb-10 mt-8 font-soehne text-serviceBody font-normal leading-[150%] text-navy tablet:mb-16">
          {data.introParagraph}
        </p>
        <div className="grid grid-cols-1 gap-8 tablet:grid-cols-3 tablet:gap-12">
          {data.items.map((item, index) => (
            <div key={index} className="w-full max-w-[404px] text-left">
              <p
                className="font-heldane text-[100px] font-[620] leading-[97%]"
                style={{ color: 'var(--Primary-Navy, #192836)' }}
              >
                {(() => {
                  const str = String(item.value).trimEnd();
                  const lastChar = str.slice(-1);
                  if (lastChar.toLowerCase() === 'x') {
                    return (
                      <>
                        {str.slice(0, -1)}
                        <span
                          className="inline-block italic"
                          style={{ fontStyle: 'italic', transform: 'skewX(-8deg)' }}
                        >
                          {lastChar}
                        </span>
                      </>
                    );
                  }
                  return item.value;
                })()}
              </p>
              <p
                className="mt-2 font-soehne text-serviceBody font-normal leading-[150%]"
                style={{ color: 'var(--Primary-Navy, #192836)' }}
              >
                {item.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const ICON_GRID_SOURCES = [
  '/images/services/company_icons/Logo_Carrot.svg',
  '/images/services/company_icons/Logo_All-Turtles.svg',
  '/images/services/company_icons/Logo_Octane.svg',
  '/images/services/company_icons/Logo_Sora.svg',
  '/images/services/company_icons/Logo_Sunflower.svg',
  '/images/services/company_icons/Logo_Surfboard.svg',
  '/images/services/company_icons/Logo_Bentoville.svg',
  '/images/services/company_icons/Logo_Sift-wordmark.svg',
  '/images/services/company_icons/Logo_Twisty-Tongue.svg',
] as const;

function IconGridSection() {
  const { iconGrid: data } = content;
  return (
    <section className="bg-navy py-12 tablet:py-24">
      <div className="mx-auto w-full max-w-[1328px] px-4 tablet:px-12">
        <div className="mb-6 flex translate-x-0 transform justify-center tablet:mb-8 tablet:translate-x-[140px]">
          <ServiceAllCapsTitle title={data.sectionTitle} />
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1328px] px-4 tablet:px-12">
        <div className="-mx-4 w-[calc(100%+2rem)] tablet:mx-0 tablet:w-full">
          <div className="hidden w-full tablet:flex">
            <div className="h-[72px] min-w-0 flex-1 bg-at tablet:h-20" />
            <div className="mt-8 hidden w-[390px] shrink-0 bg-navy tablet:block" aria-hidden />
          </div>

          <div className="relative flex w-full min-w-0 flex-col tablet:min-h-0 tablet:flex-row">
            {/* Red column: on tablet, absolute so row height is driven by white; red matches that height. No padding so centering is over the full red box. */}
            <div className="order-1 flex flex-1 items-center justify-center overflow-hidden bg-at p-4 tablet:p-0">
              <div className="tablet:py-auto grid grid-cols-3 grid-rows-3 gap-x-[48px] gap-y-[71px] py-16 tablet:gap-x-[64px] tablet:gap-y-[71px]">
                {ICON_GRID_SOURCES.map((src, i) => (
                  <div
                    key={i}
                    className="relative flex size-[72px] shrink-0 items-center justify-center tablet:size-[96px]"
                    aria-hidden
                  >
                    <Image
                      src={src}
                      alt=""
                      width={96}
                      height={96}
                      className="h-full w-full object-contain [filter:brightness(0)_invert(1)]"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="relative z-10 order-2 bg-white p-12 tablet:-ml-[197px] tablet:ml-auto tablet:max-w-[640px]">
              <div className="space-y-4">
                {data.paragraphs.map((paragraph, index) => (
                  <p key={index} className="font-soehne text-serviceBody font-normal leading-[150%] text-navy">
                    {renderParagraphWithBold(paragraph)}
                  </p>
                ))}
              </div>
            </div>
          </div>

          <div className="hidden w-full tablet:flex">
            <div className="h-[72px] min-w-0 flex-1 bg-at tablet:h-20" />
            <div className="hidden w-[390px] shrink-0 bg-navy tablet:block" aria-hidden />
          </div>
        </div>
      </div>
    </section>
  );
}

function ProcessSection() {
  const { process: data } = content;
  return (
    <section className="bg-navy py-12 tablet:py-24">
      <div className="mx-auto w-full max-w-[936px] px-4 tablet:px-12">
        <div>
          <ServiceAllCapsTitle title="HOW IT WORKS" />
        </div>
        <div className="mb-10 mt-8 tablet:mb-24">
          <p
            className="font-soehne text-serviceBody font-normal leading-[150%]"
            style={{ color: 'var(--UX-White, #FFF)' }}
          >
            {data.paragraph}
          </p>
        </div>
        <div className="ml-6 grid grid-cols-1 gap-8 tablet:ml-0 tablet:grid-cols-2 tablet:gap-x-16 tablet:gap-y-24">
          {data.steps.map((step, index) => (
            <div key={index}>
              <h4 className="mb-2 font-heldane text-[36px] font-normal leading-[97%] text-white tablet:mb-3">
                {step.title}
              </h4>
              <p className="font-soehne text-serviceBody font-normal leading-[150%] text-white">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CaseStudySection() {
  const { caseStudy: data } = content;

  return (
    <section className="relative w-full overflow-x-hidden overflow-y-visible bg-navy tablet:min-h-[1086px]">
      {/* Flex container: mobile = column with order (title, image, content); tablet = block with absolute image */}
      <div className="flex flex-col tablet:block">
        {/* Image: block on mobile, absolute background on tablet */}
        {data.backgroundImage && (
          <div className="relative order-2 w-full tablet:absolute tablet:bottom-0 tablet:left-1/2 tablet:top-[96px] tablet:min-h-[990px] tablet:w-full tablet:max-w-[1200px] tablet:-translate-x-1/2">
            {/* Mobile: in-flow block image */}
            <div className="relative aspect-[3/2] w-full tablet:hidden">
              <Image
                src={data.backgroundImage}
                alt=""
                fill
                className="object-cover object-center"
                priority
                sizes="100vw"
              />
            </div>
            {/* Tablet: full-height image from 96px to section bottom, min 990px tall */}
            <div className="relative hidden h-full min-h-[990px] w-full max-w-[1078px] tablet:block">
              <Image
                src={data.backgroundImage}
                alt=""
                fill
                className="object-cover object-center"
                priority
                sizes="(max-width: 1200px) 100vw, 1078px"
                unoptimized
              />
            </div>
          </div>
        )}

        {/* Content: title (tablet only) + white box. Mobile: edge-to-edge, no gap above; tablet: 192px from section top */}
        <div className="relative z-10 order-3 flex justify-center px-0 tablet:order-none tablet:mt-[192px] tablet:px-0">
          <div className="flex w-full max-w-full flex-col tablet:w-full tablet:max-w-[1328px] tablet:flex-row tablet:justify-end">
            <div className="w-full tablet:w-full tablet:max-w-[640px]">
              <div className="mt-0 w-full bg-white p-6 tablet:w-full tablet:max-w-[640px] tablet:p-12">
                <p className="mb-6 font-heldane text-[48px] font-normal leading-[56px] text-navy">{data.title}</p>
                <p className="mb-6 font-soehne text-serviceBody font-normal leading-[150%] text-navy">
                  {data.paragraphLink
                    ? (() => {
                        const { text, url } = data.paragraphLink!;
                        const i = data.paragraph.indexOf(text);
                        if (i === -1) return data.paragraph;
                        return (
                          <>
                            {data.paragraph.slice(0, i)}
                            <Link
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-at underline hover:no-underline"
                            >
                              {text}
                            </Link>
                            {data.paragraph.slice(i + text.length)}
                          </>
                        );
                      })()
                    : data.paragraph}
                </p>
                <blockquote className="mb-6 font-soehne text-serviceBody font-normal italic leading-[150%] text-navy">
                  &ldquo;{data.quote}&rdquo;
                </blockquote>
                <p
                  className="font-soehne text-serviceBody font-bold leading-[150%]"
                  style={{ color: 'var(--Primary-Navy,hsl(209, 36.70%, 15.50%))' }}
                >
                  {data.testimonialPerson}
                </p>
                <p className="font-soehne text-serviceBody leading-[150%] text-navy">{data.testimonialTitle}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const { faq: data } = content;
  return (
    <section className="bg-navy py-12 tablet:py-24" aria-label="FAQ">
      <div className="mx-auto w-full max-w-[1328px] px-4 tablet:px-12">
        <div className="pb-6 tablet:pb-10">
          <ServiceAllCapsTitle title={data.sectionTitle} wrapOnMobile />
        </div>
        <div className="space-y-2 border-t border-white/20">
          {data.items.map((item, index) => (
            <details key={index} className="group border-b border-white/20">
              <summary className="cursor-pointer list-none py-4 font-soehne text-serviceSectionHeading font-semibold leading-[150%] text-white transition-colors hover:text-at [&::-webkit-details-marker]:hidden">
                <span className="flex items-center justify-between gap-4">
                  {item.question}
                  <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center text-xl font-light text-white"
                    aria-hidden
                  >
                    <span className="group-open:hidden">+</span>
                    <span className="hidden group-open:inline">−</span>
                  </span>
                </span>
              </summary>
              {item.answer.paragraphs.map((paragraph, index) => (
                <p key={index} className="pb-4 font-soehne text-serviceBody font-normal leading-[150%] text-white/90">
                  {renderParagraphWithBold(paragraph)}
                </p>
              ))}
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

type Props = Record<string, never>;

export const ServicePage = (_props: Props) => {
  const { layout, hero, portfolioShowcase } = content;
  const [mounted, setMounted] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setImageLoaded(true);
  };

  return (
    <ServiceLayout>
      <div className="relative z-30">
        <Link href="/" className="block pl-5 pt-2 tablet:mb-4" aria-label="Go to All Turtles Home">
          <AdaptiveLogo block blendMode={false} color={layout.logoColor as 'text-at'} aria-hidden />
        </Link>
      </div>
      <section className="relative -mb-24 -mt-[104px] w-full overflow-x-hidden overflow-y-visible bg-navy pb-24 pt-[104px] tablet:mb-0 tablet:pb-0">
        {mounted && hero.backgroundImage && (
          <div className="absolute inset-0">
            <Image
              src={hero.backgroundImage}
              alt=""
              fill
              className={`object-cover object-center transition-opacity duration-500 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              priority
              sizes="100vw"
              onLoad={handleImageLoad}
              unoptimized
            />
          </div>
        )}

        <div
          ref={contentRef}
          className="relative z-10 mx-auto flex w-full max-w-[1482px] flex-col items-center justify-center px-4 pb-6 pt-16 text-center tablet:px-12 tablet:pb-[184px] tablet:pt-24"
        >
          {hero.heading && (
            <p
              className="text-center font-breit font-normal uppercase leading-[120%] text-at [font-size:clamp(18px,calc(18px+10*(100vw-767px)/715),28px)]"
              style={{ textShadow: '0 0 24px #192836' }}
            >
              {hero.heading.toUpperCase()}
            </p>
          )}
          {hero.headingStatement && (
            <h1
              className="text-center font-heldane font-normal leading-[120%] [font-size:clamp(40px,calc(40px+60*(100vw-767px)/715),100px)]"
              style={{ color: 'var(--UX-White, #FFF)' }}
            >
              {hero.headingStatement}
            </h1>
          )}
        </div>
      </section>
      <div className="relative z-20">
        <IntroSection />
        <StatsSection />
        <IconGridSection />
        <ProcessSection />
        <CaseStudySection />
        <CaseStudyCTA ctaLink={content.caseStudy.ctaLink} ctaText={content.caseStudy.ctaText} />
        <PortfolioShowcase variant="light" items={portfolioShowcase} title="PORTFOLIO SHOWCASE" />
        <FAQSection />
        <div className="mx-auto max-w-[1328px] p-4 tablet:px-12">
          <p className="text-serviceBodySmall font-soehne italic leading-[150%] text-white/50">
            The services described herein do not constitute legal advice, legal opinions, or legal services. All Turtles
            Corporation (ATC) is not a law firm and does not practice law or act as a patent attorney or patent agent.
            <br />
            <br />
            All patent and trademark filings, prosecution activities, and related legal services are performed by, or
            under the supervision of, licensed legal counsel. References to third-party law firms reflect independent
            professional relationships and do not imply that ATC provides legal services.
            <br />
            <br />
            ATC may decline to perform requested services where doing so would conflict with applicable legal
            obligations or otherwise create legal, ethical, or reputational risk.
          </p>
        </div>
      </div>
    </ServiceLayout>
  );
};
